import { CITIES } from '@/config/cities';

/**
 * preload.ts — warm a city's race assets BEFORE the race needs them (M52).
 * The moment a match is found (or the next tour city is announced) we fire
 * low-priority fetches for that city's landmark models, sky image and the
 * shared PBR textures. They land in the HTTP + service-worker caches, so when
 * the world actually builds seconds later the loads are local — no mid-countdown
 * hitches and no pop-in on a first visit.
 *
 * Fire-and-forget by design: a failed warm just means "not warmed" (the real
 * loader fetches as usual), never an error the player sees.
 *
 * CITY_LANDMARK_MODELS mirrors the per-city registries in src/world/landmarks/
 * (the URLs there live inside build() closures, so they can't be introspected
 * without building meshes). If a landmark is added there, add its file here —
 * missing entries only cost the prefetch, nothing breaks.
 */
const CITY_LANDMARK_MODELS: Record<string, string[]> = {
  nyc: [
    'apollo',
    'belvedere_castle',
    'bethesda_fountain',
    'bow_bridge',
    'chrysler',
    'empire_state',
    'hamilton_grange',
    'langston_hughes',
    'nye_ball',
  ],
  london: ['big_ben', 'gherkin', 'london_eye', 'shard', 'st_pauls', 'tower_bridge', 'tower_of_london'],
  napoli: ['castel_dell_ovo', 'castel_nuovo', 'galleria', 'plebiscito', 'vesuvius'],
  tokyo: ['fuji', 'kaminarimon', 'pagoda', 'shibuya_tower', 'tokyo_tower', 'torii'],
  rio: ['arcos_da_lapa', 'christ_redeemer', 'maracana', 'sugarloaf'],
  dakar: ['giant_baobab', 'goree_house', 'grande_mosquee', 'rally_arch', 'renaissance_monument'],
  lagos: ['cocoa_house', 'lagoon_lighthouse', 'lekki_ikoyi', 'national_theatre'],
  mumbai: ['cst', 'gateway_of_india', 'haji_ali_dargah', 'sea_link', 'taj_hotel'],
  patagonia: ['estancia_gate', 'fitz_roy', 'glacier_wall'],
};

/** Shared across every city (road PBR set + the landmark material albedos). */
const SHARED_TEXTURES = [
  'textures/asphalt_02/diffuse.jpg',
  'textures/asphalt_02/normal.jpg',
  'textures/asphalt_02/arm.jpg',
  'textures/lm_brick/albedo.jpg',
  'textures/lm_stone/albedo.jpg',
  'textures/lm_concrete/albedo.jpg',
  'textures/lm_plaster/albedo.jpg',
  'textures/lm_rooftile/albedo.jpg',
  'textures/lm_wood/albedo.jpg',
  // The shared photographic foliage set (tree round) — every city's trees,
  // palms, pines and baobabs. Alpha-cut canopies have nothing to test until
  // these land, so warming them matters more than the landmark albedos.
  'textures/foliage/foliage_atlas.webp',
  'textures/foliage/palm_frond.webp',
  'textures/foliage/bark.webp',
];

const warmed = new Set<string>();

export function warmCityAssets(cityId: string): void {
  if (typeof fetch === 'undefined') return; // headless tests
  const city = CITIES.find((c) => c.id === cityId);
  if (!city) return;
  const urls: string[] = [
    ...(CITY_LANDMARK_MODELS[cityId] ?? []).map((m) => `models/landmarks/${m}.glb`),
    ...SHARED_TEXTURES,
  ];
  if (city.theme.env?.hdri) urls.push(city.theme.env.hdri);
  const base = import.meta.env?.BASE_URL ?? '/';
  for (const u of urls) {
    if (warmed.has(u)) continue;
    warmed.add(u);
    // 'low' keeps the warm from competing with anything the page needs NOW.
    fetch(base + u, { priority: 'low' } as RequestInit).catch(() => warmed.delete(u));
  }
}
